(function () {
  // getElementById returns the FIRST #jt-phone-blocker-config. Both live stores
  // carry two: a stale copy hardcoded into the theme during an earlier cache-bust,
  // sitting ahead of the real app-embed block. So the gate was reading dead
  // markup — which is why the resend-cooldown setting never took effect, the
  // stale copy does not carry that attribute.
  //
  // Merge every copy instead, later winning for the keys it defines and earlier
  // ones filling gaps. The app embed renders after theme markup and is the copy
  // a merchant can actually edit, so its values take precedence.
  const configNodes = Array.from(
    document.querySelectorAll("#jt-phone-blocker-config"),
  );
  if (configNodes.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(
      `[jt-phone-gate] ${configNodes.length} config blocks on this page; merging, last wins. Remove the stale copy from the theme.`,
    );
  }
  const gateConfig = {};
  for (const node of configNodes) {
    for (const [key, value] of Object.entries(node.dataset)) {
      if (value !== undefined && value !== "") gateConfig[key] = value;
    }
  }
  // Each store opts in through its theme settings. Checkout enforcement must
  // also bind the verification token to the delivery phone.
  const CART_GATE_ENABLED = gateConfig.gateEnabled === "true";
  if (!CART_GATE_ENABLED) return;

  // The theme release is deferred before the app embed. Whichever valid gate
  // loads first owns the page; an unavailable new asset leaves the embed intact.
  if (window.__jtPhoneBlockerLoaded) return;
  window.__jtPhoneBlockerLoaded = true;
  if (gateConfig.addressEnabled === "true")
    window.__jtPhoneGateAddressLoaded = true;

  const publicEndpointBase = "https://jt-phone-blocker-checkout.fly.dev";

  // Respect each storefront's typography, including themes without Dawn tokens.
  // Scoped styles keep theme-wide h2, input and button rules from flattening the
  // dialog's hierarchy while its families, weights and controls stay per-theme.
  const modalFontStack =
    window.getComputedStyle(document.body).fontFamily || "system-ui,sans-serif";
  const publicBlocklistPath = `${publicEndpointBase}/public/blocklist-check`;
  const publicOtpStartPath = `${publicEndpointBase}/public/otp-start`;
  const publicOtpVerifyPath = `${publicEndpointBase}/public/otp-verify`;
  const proxyPath = normalizeEndpointPath(
    gateConfig.proxyPath,
    publicBlocklistPath,
  );
  const otpEnabled = gateConfig.otpEnabled === "true";
  const otpStartPath = normalizeEndpointPath(
    gateConfig.otpStartPath,
    publicOtpStartPath,
  );
  const otpVerifyPath = normalizeEndpointPath(
    gateConfig.otpVerifyPath,
    publicOtpVerifyPath,
  );
  const blockMessage =
    gateConfig.blockMessage || "Something went wrong. Please contact support.";
  const phoneLabel = gateConfig.phoneLabel || "Mobile number";
  const verifiedPhoneStorageKey = "jt-phone-blocker:verified-phone";
  const verifiedPhoneTimestampStorageKey = "jt-phone-blocker:verified-at";
  const verifiedOtpTokenStorageKey = "jt-phone-blocker:otp-token";
  const verifiedPhoneTtlMs = 24 * 60 * 60 * 1_000;
  const configuredResendCooldownSeconds = Number(
    gateConfig.resendCooldownSeconds || "90",
  );
  const resendCooldownSeconds =
    Number.isFinite(configuredResendCooldownSeconds) &&
    configuredResendCooldownSeconds > 0
      ? Math.ceil(configuredResendCooldownSeconds)
      : 90;

  const checkoutSelectors = [
    'a[href$="/checkout"]',
    'a[href*="/checkout?"]',
    "#checkout",
    "button#checkout",
    'button[name="checkout"]',
    'input[name="checkout"]',
    'button[formaction*="/checkout"]',
    'input[formaction*="/checkout"]',
    ".cart__checkout",
    ".cart__checkout-button",
    ".cart-drawer-checkout",
    ".cart-checkout-button",
    ".codk-cart-checkout",
    "#CartDrawer-Checkout",
    "#cart-sidebar-checkout",
    ".button-checkout",
    ".shopify-payment-button",
    ".shopify-payment-button__button",
    "shopify-accelerated-checkout",
    "shopify-accelerated-checkout-cart",
  ].join(",");

  const addressEnabled = gateConfig.addressEnabled === "true";
  let deliveryAddress = null;
  let activePrompt = null;
  let suppressCheckoutClickUntil = 0;

  function normalizeEndpointPath(path, fallbackPath) {
    const configuredPath = String(path || "").trim();
    if (!configuredPath) return fallbackPath;
    if (/^\/apps\/jt-phone-blocker(?:\/|$)/.test(configuredPath)) {
      return fallbackPath;
    }
    return configuredPath;
  }

  function findCheckoutTrigger(target) {
    return target instanceof Element ? target.closest(checkoutSelectors) : null;
  }

  function isCheckoutForm(form, submitter) {
    if (!(form instanceof HTMLFormElement)) return false;
    const action = form.getAttribute("action") || "";
    const submitterAction =
      submitter instanceof HTMLElement
        ? submitter.getAttribute("formaction") || ""
        : "";

    return (
      /\/cart(?:\?|$)/.test(action) ||
      /\/checkout(?:\?|$)/.test(action) ||
      /\/checkout(?:\?|$)/.test(submitterAction) ||
      Boolean(form.querySelector('[name="checkout"]'))
    );
  }

  function isCodKingCheckoutRedirect(event) {
    return event?.data?.type === "CODK_REDIRECT_TO_CHECKOUT";
  }

  function normalizePhone(phone) {
    return phone.replace(/[^\d+]/g, "").trim();
  }

  function canonicalPhone(phone) {
    const normalized = normalizePhone(phone);
    if (normalized.startsWith("+63")) return `63${normalized.slice(3)}`;
    if (normalized.startsWith("63")) return normalized;
    if (normalized.startsWith("0") && normalized.length === 11) {
      return `63${normalized.slice(1)}`;
    }
    return normalized;
  }

  function getStoredPhone() {
    try {
      return window.localStorage.getItem(verifiedPhoneStorageKey) || "";
    } catch {
      return "";
    }
  }

  function getStoredOtpToken() {
    try {
      return window.localStorage.getItem(verifiedOtpTokenStorageKey) || "";
    } catch {
      return "";
    }
  }

  function isStoredOtpTokenFresh(token) {
    if (!token) return false;

    const parts = String(token).split(".");
    if (parts.length !== 4) return false;

    const expiresAtMs = Number(parts[1]);
    return Number.isFinite(expiresAtMs) && Date.now() < expiresAtMs;
  }

  function setStoredPhone(phone, otpToken) {
    try {
      window.localStorage.setItem(verifiedPhoneStorageKey, phone);
      window.localStorage.setItem(
        verifiedPhoneTimestampStorageKey,
        String(Date.now()),
      );
      if (otpToken) {
        window.localStorage.setItem(verifiedOtpTokenStorageKey, otpToken);
      } else {
        window.localStorage.removeItem(verifiedOtpTokenStorageKey);
      }
    } catch {
      // localStorage can be unavailable in privacy-restricted browsers.
    }
  }

  function hasFreshVerifiedPhone(phone) {
    const storedPhone = getStoredPhone();
    if (!storedPhone) return false;
    if (phone && canonicalPhone(storedPhone) !== canonicalPhone(phone)) {
      return false;
    }
    if (otpEnabled && !isStoredOtpTokenFresh(getStoredOtpToken())) {
      return false;
    }

    try {
      const verifiedAt = Number(
        window.localStorage.getItem(verifiedPhoneTimestampStorageKey) || "0",
      );
      return verifiedAt > 0 && Date.now() - verifiedAt < verifiedPhoneTtlMs;
    } catch {
      return false;
    }
  }

  function isFreshVerifiedPhone(phone) {
    return hasFreshVerifiedPhone(phone);
  }

  function isUsableButtonBackground(value) {
    if (!value) return false;

    const normalized = value.replace(/\s+/g, "").toLowerCase();
    return ![
      "transparent",
      "rgba(0,0,0,0)",
      "rgb(255,255,255)",
      "#fff",
      "#ffffff",
      "white",
    ].includes(normalized);
  }

  function normalizeThemeColor(value) {
    const trimmed = value.trim();
    if (/^\d+(?:\s*,\s*\d+){2}$/.test(trimmed)) {
      return `rgb(${trimmed})`;
    }
    return trimmed;
  }

  function getThemeButtonBackground() {
    const themeStyles = [
      window.getComputedStyle(document.body),
      window.getComputedStyle(document.documentElement),
    ];
    const rootVariables = [
      "--color-button",
      "--buttons-primary-background-color",
      "--button-background-color",
      "--button-background",
      "--color-accent",
    ];

    for (const styles of themeStyles)
      for (const variableName of rootVariables) {
        const color = normalizeThemeColor(
          styles.getPropertyValue(variableName),
        );
        if (isUsableButtonBackground(color)) return color;
      }

    const buttonSelectors = [
      'button[name="add"]',
      ".product-form__submit",
      ".cart__checkout-button",
      ".shopify-payment-button__button",
      ".button",
      "button[type='submit']",
    ].join(",");

    for (const button of Array.from(
      document.querySelectorAll(buttonSelectors),
    )) {
      const color = window.getComputedStyle(button).backgroundColor;
      if (isUsableButtonBackground(color)) return color;
    }

    return "#111827";
  }

  function showVerifiedNotice() {
    const existingNotice = document.getElementById(
      "jt-phone-blocker-verified-notice",
    );
    if (existingNotice) existingNotice.remove();

    const notice = document.createElement("div");
    notice.id = "jt-phone-blocker-verified-notice";
    notice.setAttribute("role", "status");
    notice.textContent =
      "Mobile number verified. You can continue to checkout.";
    notice.style.cssText =
      "position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483647;max-width:min(92vw,420px);border-radius:999px;background:#111827;color:#fff;box-shadow:0 12px 36px rgba(15,23,42,.28);padding:12px 18px;font:650 14px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;text-align:center;";

    document.body.append(notice);
    window.setTimeout(() => {
      notice.remove();
    }, 5_000);
  }

  let pendingProductAdd = null;

  function rememberPendingProductAdd(target) {
    pendingProductAdd = null;
    const el = target instanceof Element ? target : null;
    if (!el) return;
    const form = el.closest('form[action*="/cart/add"]');
    if (!form) return;
    const data = new FormData(form);
    if (!data.get("id")) return;
    pendingProductAdd = data;
  }

  async function flushPendingProductAdd() {
    const data = pendingProductAdd;
    pendingProductAdd = null;
    if (!data) return;
    try {
      await fetch("/cart/add.js", { method: "POST", body: data });
    } catch {
      // Fall through to /checkout regardless: an existing cart may still be
      // valid, and a hard failure here would strand the buyer entirely.
    }
  }

  async function navigateToFreshCheckout() {
    await flushPendingProductAdd();
    const destination =
      addressEnabled && window.JTAddressSelector
        ? window.JTAddressSelector.checkoutUrl(deliveryAddress)
        : "/checkout";
    window.location.assign(destination);
  }

  async function continueToCheckoutAfterGuard(guardPromise) {
    try {
      const allowed = await guardPromise;
      if (allowed) await navigateToFreshCheckout();
    } catch {
      showBlockNotice("We could not verify this number. Please try again.");
    }
  }

  function showBlockNotice(message) {
    const existingNotice = document.getElementById(
      "jt-phone-blocker-error-notice",
    );
    if (existingNotice) existingNotice.remove();

    const notice = document.createElement("div");
    notice.id = "jt-phone-blocker-error-notice";
    notice.setAttribute("role", "alert");
    notice.textContent = message || blockMessage;
    notice.style.cssText =
      "position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483647;max-width:min(92vw,420px);border-radius:999px;background:#991b1b;color:#fff;box-shadow:0 12px 36px rgba(15,23,42,.28);padding:12px 18px;font:650 14px/1.35 system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;text-align:center;";

    document.body.append(notice);
    window.setTimeout(() => {
      notice.remove();
    }, 5_000);
  }

  function formatCountdown(seconds) {
    const safeSeconds = Math.max(0, Math.ceil(seconds));
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = String(safeSeconds % 60).padStart(2, "0");
    return `${minutes}:${remainingSeconds}`;
  }

  async function getCartDetails() {
    try {
      const response = await fetch("/cart.js", {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return {};

      const cart = await response.json();
      return {
        token: typeof cart.token === "string" ? cart.token : undefined,
        itemCount:
          typeof cart.item_count === "number" ? cart.item_count : undefined,
      };
    } catch {
      return {};
    }
  }

  function resolveEndpointUrl(path) {
    return new URL(path, window.location.origin);
  }

  function isCrossOrigin(url) {
    return url.origin !== window.location.origin;
  }

  async function postEndpointJson(path, payload) {
    const endpointUrl = resolveEndpointUrl(path);
    const crossOrigin = isCrossOrigin(endpointUrl);
    const response = await fetch(endpointUrl.toString(), {
      method: "POST",
      credentials: crossOrigin ? "omit" : "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": crossOrigin
          ? "text/plain;charset=UTF-8"
          : "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      return { ok: false };
    }

    return response.json();
  }

  async function postSameOriginJson(path, payload) {
    const response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      return { ok: false };
    }

    return response.json();
  }

  async function postSameOriginFormJson(path, attributes) {
    const body = new URLSearchParams();
    for (const [key, value] of Object.entries(attributes)) {
      body.set(`attributes[${key}]`, String(value));
    }

    const response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
      body,
    });

    if (!response.ok) {
      return { ok: false };
    }

    return response.json();
  }

  async function startOtp(phone) {
    const cart = await getCartDetails();
    return postEndpointJson(otpStartPath, {
      phone,
      shopDomain: window.location.hostname,
      cartToken: cart.token,
      cartTotalQuantity: cart.itemCount,
    });
  }

  async function verifyOtp(challengeId, phone, code) {
    return postEndpointJson(otpVerifyPath, {
      challengeId,
      phone,
      code,
    });
  }

  async function writeOtpCartAttribute(token) {
    if (!token) return false;

    const attributes = {
      jt_phone_otp_token: token,
    };
    const response = await postSameOriginJson("/cart/update.js", {
      attributes,
    });
    if (response.ok !== false) return true;

    const formResponse = await postSameOriginFormJson(
      "/cart/update.js",
      attributes,
    );
    if (formResponse.ok === false) return false;

    return true;
  }

  async function reuseStoredOtpVerification(phone) {
    const storedPhone = getStoredPhone();
    const storedOtpToken = getStoredOtpToken();
    if (!storedPhone || !isStoredOtpTokenFresh(storedOtpToken)) {
      return { ok: false, blocked: false };
    }
    if (phone && canonicalPhone(storedPhone) !== canonicalPhone(phone)) {
      return { ok: false, blocked: false };
    }

    const phoneToCheck = phone || storedPhone;
    const check = await checkPhone(phoneToCheck);
    if (check.blocked) {
      return {
        ok: true,
        blocked: true,
        message: check.message || blockMessage,
      };
    }
    if (!check.ok) return { ok: false, blocked: false };

    const cartAttributeWritten = await writeOtpCartAttribute(storedOtpToken);
    if (!cartAttributeWritten) return { ok: false, blocked: false };

    setStoredPhone(phoneToCheck, storedOtpToken);
    return { ok: true, blocked: false };
  }

  function addPromptStyles() {
    if (document.getElementById("jt-gate-styles")) return;
    const style = document.createElement("style");
    style.id = "jt-gate-styles";
    style.textContent = `
      .jt-gate-overlay {
        --jt-gate-font:var(--font-body-family,${modalFontStack});
        --jt-gate-ink:var(--color-foreground,24,24,24);
        --jt-gate-surface:var(--color-background,250,249,248);
        --jt-gate-input-radius:var(--inputs-radius,0px);
        --jt-gate-button-radius:var(--buttons-radius,0px);
        --jt-gate-pad:28px;
        position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;
        padding:20px;background:rgba(var(--jt-gate-ink),.48);font-family:var(--jt-gate-font);
      }
      .jt-gate-overlay * {box-sizing:border-box;}
      .jt-gate-panel {
        width:min(100%,520px);max-height:calc(100dvh - 40px);display:flex;flex-direction:column;overflow:hidden;
        border:1px solid rgba(var(--jt-gate-ink),.1);border-radius:8px;
        background:rgb(var(--jt-gate-surface));color:rgb(var(--jt-gate-ink));
        box-shadow:0 24px 72px rgba(var(--jt-gate-ink),.2);
        font-family:var(--jt-gate-font);font-size:1em;font-style:var(--font-body-style,normal);
        font-weight:var(--font-body-weight,400);line-height:1.5;letter-spacing:normal;
      }
      .jt-gate-panel .jt-gate-content {padding:var(--jt-gate-pad);min-height:0;overflow:auto;overscroll-behavior:contain;}
      .jt-gate-panel [hidden] {display:none!important;}
      .jt-gate-panel .jt-gate-title {
        margin:0 0 10px;font-family:var(--font-heading-family,var(--jt-gate-font));
        font-style:var(--font-heading-style,normal);font-weight:var(--font-heading-weight,400);
        font-size:1.625em;line-height:1.25;letter-spacing:.025em;color:inherit;
      }
      .jt-gate-panel .jt-gate-copy {margin:0 0 22px;font-size:.875em;line-height:1.65;color:rgba(var(--jt-gate-ink),.75);}
      .jt-gate-panel :is(input,button,label,p) {font-family:var(--jt-gate-font);}
      .jt-gate-panel .jt-gate-label {display:block;margin:0 0 7px;font-size:.875em;font-weight:var(--font-body-weight,400);line-height:1.5;}
      .jt-gate-panel .jt-gate-code-label {margin-top:20px;}
      .jt-gate-panel .jt-gate-input {
        width:100%;min-width:0;height:48px;padding:12px 14px;
        border:var(--inputs-border-width,1px) solid rgba(var(--jt-gate-ink),var(--inputs-border-opacity,.55));
        border-radius:var(--jt-gate-input-radius);background:rgb(var(--jt-gate-surface));color:inherit;
        font-size:1em;font-weight:var(--font-body-weight,400);line-height:1.4;letter-spacing:normal;box-shadow:none;
      }
      .jt-gate-panel .jt-gate-code {letter-spacing:.16em;font-variant-numeric:tabular-nums;}
      .jt-gate-panel .jt-gate-input:hover:not(:disabled) {border-color:rgba(var(--jt-gate-ink),.85);}
      .jt-gate-panel input:disabled {background:rgba(var(--jt-gate-ink),.04);color:rgba(var(--jt-gate-ink),.6);cursor:not-allowed;}
      .jt-gate-panel :is(input,button,a):focus-visible {outline:2px solid rgba(var(--jt-gate-ink),.85);outline-offset:3px;}
      .jt-gate-panel .jt-gate-error {margin:14px 0 0;color:oklch(47% .17 27);font-size:.8125em;line-height:1.5;}
      .jt-gate-panel .jt-gate-error:empty {display:none;}
      .jt-gate-panel .jt-gate-actions {
        display:flex;flex-wrap:wrap;flex-shrink:0;gap:12px;align-items:center;margin:0;
        padding:16px var(--jt-gate-pad);border-top:1px solid rgba(var(--jt-gate-ink),.12);
        background:rgb(var(--jt-gate-surface));
      }
      .jt-gate-panel .jt-gate-button {
        display:inline-flex;align-items:center;justify-content:center;min-height:48px;padding:12px 20px;
        border:1px solid rgba(var(--jt-gate-ink),.55);border-radius:var(--jt-gate-button-radius);
        background:rgb(var(--jt-gate-surface));color:inherit;font-size:.9375em;
        font-weight:var(--font-body-weight,400);line-height:1.4;letter-spacing:.05em;cursor:pointer;
      }
      .jt-gate-panel .jt-gate-back {min-width:88px;}
      .jt-gate-panel .jt-gate-primary {flex:1;min-width:140px;border-color:transparent;}
      .jt-gate-panel .jt-gate-button:hover:not(:disabled) {box-shadow:inset 0 0 0 1px currentColor;}
      .jt-gate-panel button:disabled {opacity:.6;cursor:wait;}
      .jt-gate-panel .jt-gate-link {
        border:0;border-radius:0;padding:4px 0;background:transparent;color:inherit;text-decoration:underline;
        text-underline-offset:.25em;font-size:.8125em;font-weight:var(--font-body-weight,400);line-height:1.6;cursor:pointer;
      }
      .jt-gate-panel .jt-gate-resend {order:3;flex-basis:100%;justify-content:center;min-height:44px;}
      .jt-gate-panel .jt-gate-skip {order:4;flex-basis:100%;justify-content:center;min-height:44px;font-size:.8125em;font-weight:var(--font-body-weight,400);}
      .jt-gate-panel .jt-gate-summary {margin:0 0 4px;font-size:.875em;line-height:1.65;overflow-wrap:anywhere;}
      .jt-gate-panel .jt-gate-edit {margin:0 0 20px;min-height:32px;}
      @media(max-width:540px) {
        .jt-gate-overlay {padding:12px;--jt-gate-pad:20px;}
        .jt-gate-panel {max-height:calc(100dvh - 24px);}
        .jt-gate-panel .jt-gate-title {font-size:1.5em;}
      }
    `;
    document.head.append(style);
  }

  function renderPrompt(prefillPhone) {
    // With OTP suspended, Shopify collects and screens the delivery phone.
    // Keep the address helper without showing a separate verification prompt.
    if (!otpEnabled && (!addressEnabled || !window.JTAddressSelector)) {
      return Promise.resolve(true);
    }
    if (activePrompt) return activePrompt;
    addPromptStyles();

    activePrompt = new Promise((resolve) => {
      let otpChallengeId = null;
      let currentPhone = "";
      let resendCooldownTimer = null;
      let closed = false;
      let addressStep = addressEnabled && Boolean(window.JTAddressSelector);
      const previouslyFocused = document.activeElement;
      const previousOverflow = document.body.style.overflow;
      const addressForm = addressStep
        ? window.JTAddressSelector.create({
            indexUrl: gateConfig.addressIndexUrl,
            prefill: deliveryAddress,
          })
        : null;

      const overlay = document.createElement("div");
      overlay.className = "jt-gate-overlay";
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");
      overlay.setAttribute("aria-labelledby", "jt-gate-heading");
      // Stable hook for the hourly probe. It used to match the modal's title
      // text, so renaming the copy silently broke monitoring — the gate kept
      // working while the probe reported buyers were going through unverified.
      overlay.setAttribute("data-jt-phone-gate", "1");
      const panel = document.createElement("div");
      panel.className = "jt-gate-panel";

      const title = document.createElement("h2");
      title.className = "jt-gate-title";
      title.id = "jt-gate-heading";
      title.textContent = "Confirm Your Number";
      const copy = document.createElement("p");
      copy.className = "jt-gate-copy";
      copy.textContent =
        "Ilagay po ang inyong number para ma-contact po kayo ng rider sa delivery day.";
      const label = document.createElement("label");
      label.className = "jt-gate-label";
      label.textContent = phoneLabel;
      label.htmlFor = "jt-gate-mobile";
      const input = document.createElement("input");
      input.className = "jt-gate-input";
      input.id = "jt-gate-mobile";
      input.type = "tel";
      input.autocomplete = "tel";
      input.inputMode = "tel";
      input.placeholder = "09XXXXXXXXX";
      input.value = prefillPhone || getStoredPhone();
      const codeLabel = document.createElement("label");
      codeLabel.className = "jt-gate-label jt-gate-code-label";
      codeLabel.textContent = "Verification code";
      codeLabel.htmlFor = "jt-gate-code";
      codeLabel.style.display = "none";

      const codeInput = document.createElement("input");
      codeInput.className = "jt-gate-input jt-gate-code";
      codeInput.id = "jt-gate-code";
      codeInput.type = "text";
      codeInput.autocomplete = "one-time-code";
      codeInput.inputMode = "numeric";
      codeInput.placeholder = "123456";
      codeInput.maxLength = 8;
      codeInput.style.display = "none";

      const error = document.createElement("p");
      error.className = "jt-gate-error";
      error.setAttribute("role", "alert");
      const actions = document.createElement("div");
      actions.className = "jt-gate-actions";

      const cancel = document.createElement("button");
      cancel.className = "jt-gate-button jt-gate-back";
      cancel.type = "button";
      cancel.textContent = "Back";
      const continueButton = document.createElement("button");
      continueButton.className = "jt-gate-button jt-gate-primary";
      continueButton.type = "button";
      continueButton.textContent = otpEnabled ? "Send code" : "Continue";
      continueButton.style.background = getThemeButtonBackground();

      const resendButton = document.createElement("button");
      resendButton.className = "jt-gate-link jt-gate-resend";
      resendButton.type = "button";
      resendButton.textContent = "Resend code";
      resendButton.style.display = "none";

      const skipAddress = document.createElement("button");
      skipAddress.className = "jt-gate-link jt-gate-skip";
      skipAddress.type = "button";
      skipAddress.textContent = "Skip – I’ll type my address at checkout";
      skipAddress.hidden = !addressForm || otpEnabled;
      skipAddress.addEventListener("click", () => {
        if (otpEnabled || !addressStep) return;
        // Skip only the helper. The existing cart handoff and Shopify's native
        // address, blocked-phone and cart validations remain in charge.
        deliveryAddress = null;
        close(true);
      });

      function stopResendCooldown() {
        if (resendCooldownTimer) {
          window.clearInterval(resendCooldownTimer);
          resendCooldownTimer = null;
        }
      }

      function resetResendButton() {
        stopResendCooldown();
        resendButton.disabled = false;
        resendButton.textContent = "Resend code";
      }

      function startResendCooldown() {
        stopResendCooldown();

        let remainingSeconds = resendCooldownSeconds;
        resendButton.disabled = true;

        const tick = () => {
          if (remainingSeconds <= 0) {
            resetResendButton();
            return;
          }

          resendButton.textContent = `Resend code in ${formatCountdown(
            remainingSeconds,
          )}`;
          remainingSeconds -= 1;
        };

        tick();
        resendCooldownTimer = window.setInterval(tick, 1_000);
      }

      function close(value) {
        if (closed) return;
        closed = true;
        addressForm?.destroy();
        document.body.style.overflow = previousOverflow;
        if (previouslyFocused?.isConnected) previouslyFocused.focus();
        stopResendCooldown();
        overlay.remove();
        activePrompt = null;
        resolve(value);
      }

      const phoneSection = document.createElement("div");
      phoneSection.append(label, input, codeLabel, codeInput);
      const addressSummary = document.createElement("p");
      addressSummary.className = "jt-gate-summary";
      const editAddress = document.createElement("button");
      editAddress.className = "jt-gate-link jt-gate-edit";
      editAddress.type = "button";
      editAddress.textContent = "Edit address";
      function showAddressStep() {
        addressStep = true;
        title.textContent = "Delivery address";
        copy.textContent = otpEnabled
          ? "Step 1 of 2 · Piliin ang tamang lugar para madaling mahanap ng rider."
          : "Piliin ang tamang lugar para madaling mahanap ng rider.";
        addressForm.element.hidden = false;
        phoneSection.hidden = true;
        addressSummary.hidden = true;
        editAddress.hidden = true;
        skipAddress.hidden = otpEnabled;
        resendButton.style.display = "none";
        cancel.textContent = "Back";
        continueButton.textContent = otpEnabled
          ? "Continue"
          : "Continue to checkout";
        error.textContent = "";
        addressForm.focus();
      }
      function showPhoneStep() {
        addressStep = false;
        title.textContent = "Confirm Your Number";
        copy.textContent = otpChallengeId
          ? "Enter the 6-digit code sent to your mobile number. The code expires in 10 minutes."
          : "Step 2 of 2 · I-verify ang number na gagamitin ng rider sa delivery. Use this same number in Shopify checkout.";
        addressForm.element.hidden = true;
        phoneSection.hidden = false;
        addressSummary.hidden = false;
        editAddress.hidden = false;
        skipAddress.hidden = true;
        addressSummary.textContent = [
          deliveryAddress.street,
          deliveryAddress.barangay,
          deliveryAddress.city,
          deliveryAddress.province,
        ].join(", ");
        resendButton.style.display = otpChallengeId ? "inline-flex" : "none";
        continueButton.textContent = otpChallengeId
          ? "Verify code"
          : otpEnabled
            ? "Send code"
            : "Continue";
        error.textContent = "";
        (otpChallengeId ? codeInput : input).focus();
      }
      editAddress.addEventListener("click", showAddressStep);
      cancel.addEventListener("click", () => close(null));
      overlay.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          close(null);
        }
        if (event.key !== "Tab") return;
        const focusable = Array.from(
          panel.querySelectorAll("input,button,a[href]"),
        ).filter((node) => !node.disabled && node.getClientRects().length > 0);
        const first = focusable[0],
          last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      });
      continueButton.addEventListener("click", async () => {
        if (addressStep) {
          const result = addressForm.read();
          if (!result.ok) {
            error.textContent = result.message;
            return;
          }
          deliveryAddress = result.address;
          if (!otpEnabled) {
            close(true);
            return;
          }
          showPhoneStep();
          // Returning verified buyers still review their address. Recheck the
          // blocklist and write the signed token to this cart before continuing.
          if (isFreshVerifiedPhone(input.value)) {
            continueButton.disabled = true;
            continueButton.textContent = "Checking…";
            const stored = await reuseStoredOtpVerification(input.value);
            if (closed) return;
            continueButton.disabled = false;
            continueButton.textContent = "Send code";
            if (stored.blocked)
              error.textContent = stored.message || blockMessage;
            else if (stored.ok) close(normalizePhone(input.value));
          }
          return;
        }
        const phone = normalizePhone(input.value);
        if (!phone) {
          error.textContent = "Enter a mobile number to continue.";
          input.focus();
          return;
        }

        continueButton.disabled = true;
        error.textContent = "";

        if (otpEnabled && !otpChallengeId) {
          continueButton.textContent = "Sending...";

          if (isFreshVerifiedPhone(phone)) {
            const storedVerification = await reuseStoredOtpVerification(phone);
            continueButton.disabled = false;
            continueButton.textContent = "Send code";

            if (storedVerification.blocked) {
              error.textContent = storedVerification.message || blockMessage;
              return;
            }
            if (storedVerification.ok) {
              close(phone);
              return;
            }
            continueButton.disabled = true;
            continueButton.textContent = "Sending...";
          }

          const otp = await startOtp(phone);
          continueButton.disabled = false;
          continueButton.textContent = "Verify code";

          if (otp.blocked) {
            error.textContent = otp.message || blockMessage;
            continueButton.textContent = "Send code";
            return;
          }

          if (otp.enabled === false) {
            const check = await checkPhone(phone);
            if (check.blocked) {
              error.textContent = check.message || blockMessage;
              continueButton.textContent = "Send code";
              return;
            }
            if (!check.ok) {
              error.textContent =
                "We could not verify this number. Please try again.";
              continueButton.textContent = "Send code";
              return;
            }
            setStoredPhone(phone);
            close(phone);
            return;
          }

          if (otp.ok && otp.verified && otp.reused && otp.token) {
            continueButton.textContent = "Continuing...";
            const cartAttributeWritten = await writeOtpCartAttribute(otp.token);
            if (!cartAttributeWritten) {
              error.textContent =
                "We could not verify this number. Please try again.";
              continueButton.textContent = "Send code";
              return;
            }
            setStoredPhone(phone, otp.token);
            close(phone);
            return;
          }

          if (!otp.ok || !otp.challengeId) {
            error.textContent =
              otp.message || "We could not send a code. Please try again.";
            continueButton.textContent = "Send code";
            return;
          }

          otpChallengeId = otp.challengeId;
          currentPhone = phone;
          input.disabled = true;
          codeLabel.style.display = "block";
          codeInput.style.display = "block";
          resendButton.style.display = "inline-flex";
          startResendCooldown();
          copy.textContent =
            "Enter the 6-digit code sent to your mobile number. The code expires in 10 minutes.";
          if (otp.debugOtp) {
            error.textContent = `Test code: ${otp.debugOtp}`;
          }
          codeInput.focus();
          return;
        }

        if (otpEnabled && otpChallengeId) {
          continueButton.textContent = "Verifying...";
          const code = codeInput.value.replace(/\D/g, "");
          if (!code) {
            continueButton.disabled = false;
            continueButton.textContent = "Verify code";
            error.textContent = "Enter the verification code to continue.";
            codeInput.focus();
            return;
          }

          const verification = await verifyOtp(
            otpChallengeId,
            currentPhone || phone,
            code,
          );
          continueButton.disabled = false;
          continueButton.textContent = "Verify code";

          if (!verification.ok || !verification.verified) {
            error.textContent =
              verification.message || "Invalid or expired code.";
            return;
          }

          const cartAttributeWritten = await writeOtpCartAttribute(
            verification.token,
          );
          if (!cartAttributeWritten) {
            error.textContent =
              "We could not verify this number. Please try again.";
            return;
          }
          setStoredPhone(currentPhone || phone, verification.token);
          close(currentPhone || phone);
          return;
        }

        continueButton.textContent = "Checking...";

        const check = await checkPhone(phone);
        continueButton.disabled = false;
        continueButton.textContent = "Continue";

        if (check.blocked) {
          error.textContent = check.message || blockMessage;
          return;
        }

        if (!check.ok) {
          error.textContent =
            "We could not verify this number. Please try again.";
          return;
        }

        setStoredPhone(phone);
        close(phone);
      });

      resendButton.addEventListener("click", async () => {
        if (!otpEnabled || !otpChallengeId || !currentPhone) return;

        resendButton.disabled = true;
        continueButton.disabled = true;
        resendButton.textContent = "Resending...";
        error.textContent = "";

        const otp = await startOtp(currentPhone);

        continueButton.disabled = false;

        if (otp.blocked) {
          resetResendButton();
          error.textContent = otp.message || blockMessage;
          return;
        }

        if (otp.ok && otp.verified && otp.reused && otp.token) {
          const cartAttributeWritten = await writeOtpCartAttribute(otp.token);
          if (!cartAttributeWritten) {
            resetResendButton();
            error.textContent =
              "We could not verify this number. Please try again.";
            return;
          }
          setStoredPhone(currentPhone, otp.token);
          close(currentPhone);
          return;
        }

        if (!otp.ok || !otp.challengeId) {
          resetResendButton();
          error.textContent =
            otp.message || "We could not send a code. Please try again.";
          return;
        }

        otpChallengeId = otp.challengeId;
        codeInput.value = "";
        if (otp.debugOtp) {
          error.textContent = `Test code: ${otp.debugOtp}`;
        }
        startResendCooldown();
        codeInput.focus();
      });

      input.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          continueButton.click();
        }
      });

      codeInput.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          continueButton.click();
        }
      });

      actions.append(cancel, resendButton, continueButton);
      if (addressForm && !otpEnabled) actions.append(skipAddress);
      const content = document.createElement("div");
      content.className = "jt-gate-content";
      content.append(
        title,
        copy,
        ...(addressForm
          ? [addressForm.element, addressSummary, editAddress]
          : []),
        phoneSection,
        error,
      );
      panel.append(content, actions);
      overlay.append(panel);
      document.body.append(overlay);
      // Keep button text legible on light brand colours as well as dark ones.
      const buttonRgb = window
        .getComputedStyle(continueButton)
        .backgroundColor.match(/\d+(?:\.\d+)?/g);
      if (buttonRgb?.length >= 3) {
        const linear = buttonRgb.slice(0, 3).map((part) => {
          const component = Number(part) / 255;
          return component <= 0.04045
            ? component / 12.92
            : Math.pow((component + 0.055) / 1.055, 2.4);
        });
        const luminance =
          0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
        continueButton.style.color = luminance > 0.179 ? "#000" : "#fff";
      }
      document.body.style.overflow = "hidden";
      if (addressForm) {
        showAddressStep();
        addressForm.ready.then(() => {
          if (!closed && addressStep) addressForm.focus();
        });
      } else {
        input.focus();
      }
    });

    return activePrompt;
  }

  async function checkPhone(phone) {
    try {
      const endpointUrl = resolveEndpointUrl(proxyPath);
      endpointUrl.searchParams.set("phone", phone);
      endpointUrl.searchParams.set("shopDomain", window.location.hostname);
      const response = await fetch(endpointUrl.toString(), {
        credentials: isCrossOrigin(endpointUrl) ? "omit" : "same-origin",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        return { ok: false, blocked: false, message: null };
      }

      return response.json();
    } catch {
      return { ok: false, blocked: false, message: null };
    }
  }

  async function guardCheckout(prefillPhone) {
    const phone = await renderPrompt(prefillPhone);
    if (!phone) return false;

    if (otpEnabled) showVerifiedNotice();
    return true;
  }

  async function guardStoredCheckout(prefillPhone, onBlocked) {
    if (!otpEnabled || addressEnabled) return guardCheckout(prefillPhone);
    const storedVerification = await reuseStoredOtpVerification(prefillPhone);
    if (storedVerification.blocked) {
      if (typeof onBlocked === "function") {
        onBlocked(storedVerification.message || blockMessage);
      } else {
        showBlockNotice(storedVerification.message || blockMessage);
      }
      return false;
    }
    if (storedVerification.ok) {
      showVerifiedNotice();
      return true;
    }

    return guardCheckout(prefillPhone);
  }

  function isCodKingOtpSubmit(target) {
    const button = target instanceof Element ? target.closest("button") : null;
    if (!button) return null;

    const text = (button.textContent || "").replace(/\s+/g, " ").trim();
    if (text !== "Send Code") return null;

    return looksLikeCodKingOtpSubmit(button) ? button : null;
  }

  function looksLikeCodKingOtpSubmit(button) {
    const root =
      button.closest(
        ".codking-otp-login,.codk-otp-widget-box,[class*='codk'],[class*='codking']",
      ) || document;
    const modalText = root.textContent || "";

    return Boolean(
      document.querySelector('input[name="phone"]') ||
      document.querySelector('input[placeholder="1 (702) 123-4567"]') ||
      root.querySelector('input[name="phone"]') ||
      root.querySelector('input[type="tel"]') ||
      modalText.includes("COD King") ||
      modalText.includes("Verify Mobile Number"),
    );
  }

  function findCodKingPhoneInput(trigger) {
    const root =
      trigger.closest(
        ".codking-otp-login,.codk-otp-widget-box,[class*='codk'],[class*='codking']",
      ) || document;

    return (
      root.querySelector('input[name="phone"]') ||
      root.querySelector('input[type="tel"]') ||
      root.querySelector('input[placeholder="1 (702) 123-4567"]') ||
      Array.from(root.querySelectorAll("input")).find((input) =>
        /^\+?63|\d{10,12}/.test(input.value || ""),
      ) ||
      null
    );
  }

  function showCodKingBlockError(trigger, message) {
    const root =
      trigger.closest(
        ".codking-otp-login,.codk-otp-widget-box,[class*='codk'],[class*='codking']",
      ) ||
      trigger.parentElement ||
      document.body;
    const existing = root.querySelector("#jt-phone-blocker-codking-error");
    const error = existing || document.createElement("p");

    error.id = "jt-phone-blocker-codking-error";
    error.setAttribute("role", "alert");
    error.textContent = message || blockMessage;
    error.style.cssText =
      "margin:10px 0 0;color:#dc2626;font:500 13px/1.45 system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;";

    if (!existing) {
      const input = findCodKingPhoneInput(trigger);
      const anchor = input?.closest("label,div") || input || trigger;
      anchor.insertAdjacentElement("afterend", error);
    }
  }

  async function guardCodKingOtpSubmit(trigger) {
    const input = findCodKingPhoneInput(trigger);
    const phone = normalizePhone(input?.value || "");
    if (!phone) return;

    if (otpEnabled) {
      continueToCheckoutAfterGuard(guardCheckout(phone));
      return;
    }

    const previousText = trigger.textContent;
    trigger.disabled = true;
    trigger.textContent = "Checking...";

    const check = await checkPhone(phone);

    trigger.disabled = false;
    trigger.textContent = previousText || "Send Code";

    if (check.blocked) {
      showCodKingBlockError(trigger, check.message || blockMessage);
      return;
    }

    if (!check.ok) {
      showCodKingBlockError(
        trigger,
        "We could not verify this number. Please try again.",
      );
      return;
    }

    setStoredPhone(phone);
    showVerifiedNotice();
  }

  function guardCheckoutTrigger(prefillPhone) {
    if (hasFreshVerifiedPhone(prefillPhone)) {
      continueToCheckoutAfterGuard(guardStoredCheckout(prefillPhone));
      return;
    }

    continueToCheckoutAfterGuard(guardCheckout(prefillPhone));
  }

  function interceptCheckoutEvent(event) {
    // Dynamic checkout buttons ("Buy it now", accelerated checkout) build a
    // one-item checkout straight from the product form and never touch the
    // cart. Swallowing the click therefore loses the item, and the buyer was
    // then sent to /checkout with an empty cart — which answers 403. Capture
    // the form now so the item can be added once verification succeeds.
    rememberPendingProductAdd(event.target);
    event.preventDefault();
    event.stopImmediatePropagation();

    if (event.type !== "click") {
      suppressCheckoutClickUntil = Date.now() + 1_500;
    }

    guardCheckoutTrigger();
  }

  function handleCheckoutEarlyIntent(event) {
    const trigger = findCheckoutTrigger(event.target);
    if (!trigger) return;

    interceptCheckoutEvent(event);
  }

  function handleCheckoutClick(event) {
    const codKingOtpSubmit = isCodKingOtpSubmit(event.target);
    if (codKingOtpSubmit) {
      const codKingPhone = findCodKingPhoneInput(codKingOtpSubmit);
      const phone = normalizePhone(codKingPhone?.value || "");
      if (!phone) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (isFreshVerifiedPhone(phone)) {
        continueToCheckoutAfterGuard(
          guardStoredCheckout(phone, (message) =>
            showCodKingBlockError(codKingOtpSubmit, message),
          ),
        );
        return;
      }

      guardCodKingOtpSubmit(codKingOtpSubmit);
      return;
    }

    const trigger = findCheckoutTrigger(event.target);
    if (!trigger) return;

    if (Date.now() < suppressCheckoutClickUntil) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    interceptCheckoutEvent(event);
  }

  function handleCheckoutSubmit(event) {
    const submitEvent = event;
    const form = submitEvent.target;
    const submitter = submitEvent.submitter;
    if (!isCheckoutForm(form, submitter)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (hasFreshVerifiedPhone()) {
      continueToCheckoutAfterGuard(guardStoredCheckout());
      return;
    }

    continueToCheckoutAfterGuard(guardCheckout());
  }

  window.addEventListener("pointerdown", handleCheckoutEarlyIntent, true);
  document.addEventListener("pointerdown", handleCheckoutEarlyIntent, true);
  window.addEventListener("mousedown", handleCheckoutEarlyIntent, true);
  document.addEventListener("mousedown", handleCheckoutEarlyIntent, true);
  window.addEventListener("touchstart", handleCheckoutEarlyIntent, true);
  document.addEventListener("touchstart", handleCheckoutEarlyIntent, true);
  window.addEventListener("click", handleCheckoutClick, true);
  document.addEventListener("click", handleCheckoutClick, true);

  window.addEventListener(
    "message",
    (event) => {
      if (!isCodKingCheckoutRedirect(event)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (hasFreshVerifiedPhone()) {
        continueToCheckoutAfterGuard(guardStoredCheckout());
        return;
      }

      continueToCheckoutAfterGuard(guardCheckout());
    },
    true,
  );

  window.addEventListener("submit", handleCheckoutSubmit, true);
  document.addEventListener("submit", handleCheckoutSubmit, true);
})();
